import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { nowInAppTz } from '../../utils/appDateTime';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Loader2, FileDown, Printer, RefreshCcw, Eye, Send, Save, Play } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { handleBlobDownloadResponse } from '../../utils/blobDownload';
import { isNativePlatform } from '../../utils/device';
import { Switch } from '../ui/switch';
import AttendanceReportPreviewModal from './AttendanceReportPreviewModal';
import useBranchLabels from '../../hooks/useBranchLabels';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { formatTime12h } from '../../utils/timeFormat';
import TimeInput12h from '../ui/time-input-12h';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

function formatNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return '0';
  const num = Number(n);
  return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
}

const DELIVERY_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

const mapScheduleFromApi = (d) => ({
  enabled: d.enabled !== false && d.enabled !== 0 && d.enabled !== '0',
  delivery_day: Number(d.delivery_day) || 1,
  delivery_time: (d.delivery_time || '06:00').slice(0, 5),
  last_period_key: d.last_period_key ?? null,
  last_run_at: d.last_run_at ?? null,
  last_run_summary: d.last_run_summary ?? null,
  next_delivery_hint: d.next_delivery_hint ?? null,
  skip_reason: d.skip_reason ?? null,
  is_due_now: !!d.is_due_now,
});

function formatEmailSummary(summary = {}) {
  const emails = summary.emails_sent ?? 0;
  const emailFailed = summary.emails_failed ?? 0;
  const skipped = summary.emails_skipped_no_recipient ?? 0;
  let text = `${emails} email(s) sent`;
  if (emailFailed > 0) text += `, ${emailFailed} failed`;
  if (skipped > 0) text += `, ${skipped} skipped (no address on file)`;
  return text;
}

export default function AttendanceReportPage() {
  const { user, canManageFeature } = useAuth();
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();
  const schoolId = user?.school_id;

  const [tab, setTab] = useState('monthly');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [filters, setFilters] = useState({
    academicYearId: '',
    classId: '',
    sectionId: '',
    month: '',
  });

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ working_days: 0, rows: [], meta: {} });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStudent, setPreviewStudent] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishAllOpen, setPublishAllOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);

  const canManageReports = canManageFeature('reports');
  const [schedule, setSchedule] = useState({
    enabled: true,
    delivery_day: 1,
    delivery_time: '06:00',
    last_period_key: null,
    last_run_at: null,
    last_run_summary: null,
    next_delivery_hint: null,
    skip_reason: null,
    is_due_now: false,
  });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleRunning, setScheduleRunning] = useState(false);
  const scheduleAutoTimerRef = useRef(null);
  const scheduleAutoRunKeyRef = useRef(null);
  const autoRunToastKeyRef = useRef(null);

  const showAutoRunToast = useCallback((autoRun, title = 'Scheduled reports published') => {
    if (autoRun?.status !== 'run') return;
    const emailFailed = autoRun.emails_failed ?? 0;
    toast({
      title: emailFailed > 0 ? `${title} with email issues` : title,
      description: `${autoRun.classes_processed ?? 0} class(es), ${formatEmailSummary(autoRun)}, ${autoRun.notifications_created ?? 0} notification(s) for period ${autoRun.period_key || 'previous month'}.`,
      variant: emailFailed > 0 ? 'destructive' : 'default',
    });
  }, [toast]);

  const loadSchedule = useCallback(async () => {
    if (!schoolId) return;
    setScheduleLoading(true);
    try {
      const res = await api.get('/reports/attendance/monthly/schedule', {
        params: { school_id: schoolId },
      });
      const d = res.data?.data || {};
      setSchedule(mapScheduleFromApi(d));
      if (d.auto_run?.status === 'run') {
        const toastKey = `${d.auto_run.period_key}|${d.last_run_at}`;
        if (autoRunToastKeyRef.current !== toastKey) {
          autoRunToastKeyRef.current = toastKey;
          showAutoRunToast(d.auto_run);
        }
        scheduleAutoRunKeyRef.current = [
          schoolId,
          d.delivery_day,
          d.delivery_time,
          d.last_period_key || '',
          'done',
        ].join('|');
      }
    } catch {
      // keep defaults
    } finally {
      setScheduleLoading(false);
    }
  }, [schoolId, showAutoRunToast]);

  const saveSchedule = useCallback(async () => {
    if (!schoolId || !canManageReports) return;
    setScheduleSaving(true);
    try {
      const res = await api.post('/reports/attendance/monthly/schedule', {
        school_id: schoolId,
        enabled: schedule.enabled,
        delivery_day: schedule.delivery_day,
        delivery_time: schedule.delivery_time,
      });
      const d = res.data?.data || {};
      setSchedule(mapScheduleFromApi(d));
      const autoRun = d.auto_run;
      if (autoRun?.status === 'run') {
        showAutoRunToast(autoRun, 'Schedule saved and reports published');
      } else {
        toast({ title: 'Schedule saved', description: 'Automatic monthly delivery settings updated.' });
      }
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setScheduleSaving(false);
    }
  }, [
    schoolId,
    canManageReports,
    schedule.enabled,
    schedule.delivery_day,
    schedule.delivery_time,
    toast,
    showAutoRunToast,
  ]);

  const runDueSchedule = useCallback(async ({ silent = false } = {}) => {
    if (!schoolId || !canManageReports) return null;
    setScheduleRunning(true);
    try {
      const res = await api.post('/reports/attendance/monthly/schedule/run-due', {
        school_id: schoolId,
      });
      const run = res.data?.data?.run || {};
      const sched = res.data?.data?.schedule || {};
      setSchedule(mapScheduleFromApi(sched));
      if (run.status === 'run') {
        scheduleAutoRunKeyRef.current = [
          schoolId,
          sched.delivery_day,
          sched.delivery_time,
          sched.last_period_key || '',
          'done',
        ].join('|');
        if (!silent) {
          showAutoRunToast(run);
        }
      }
      return run;
    } catch (e) {
      if (!silent) {
        toast({
          title: 'Delivery failed',
          description: e?.response?.data?.message || e?.message || 'Try again',
          variant: 'destructive',
        });
      }
      return null;
    } finally {
      setScheduleRunning(false);
    }
  }, [schoolId, canManageReports, showAutoRunToast, toast]);

  const runScheduleNow = async () => {
    if (!schoolId || !canManageReports) return;
    setScheduleRunning(true);
    try {
      const res = await api.post('/reports/attendance/monthly/schedule/run', {
        school_id: schoolId,
      });
      const run = res.data?.data?.run || {};
      const sched = res.data?.data?.schedule || {};
      setSchedule(mapScheduleFromApi(sched));
      if (run.status === 'skipped') {
        toast({
          title: 'Nothing to publish',
          description: run.reason === 'no new period to publish'
            ? 'The latest publishable period was already delivered. Wait until the current month ends.'
            : (run.reason || 'Run was skipped.'),
        });
        return;
      }
      const classes = run.classes_processed ?? 0;
      const emailFailed = run.emails_failed ?? 0;
      const notifications = run.notifications_created ?? 0;
      toast({
        title: emailFailed > 0 ? 'Reports published with email issues' : 'Reports published',
        description: `${classes} class(es), ${formatEmailSummary(run)}, ${notifications} notification(s) for period ${run.period_key || 'previous month'}.`,
        variant: emailFailed > 0 ? 'destructive' : 'default',
      });
    } catch (e) {
      toast({
        title: 'Run failed',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setScheduleRunning(false);
    }
  };

  useEffect(() => {
    if (!schoolId) return;
    loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  // Fallback when server cron isn't configured:
  // - If schedule is due now, trigger run-due (manage) or reload schedule (GET auto-runs).
  // - If delivery time is later today (IST), schedule a one-time reload at that time.
  // - Poll every 5 minutes on the monthly tab while enabled.
  useEffect(() => {
    if (scheduleAutoTimerRef.current) {
      clearTimeout(scheduleAutoTimerRef.current);
      scheduleAutoTimerRef.current = null;
    }

    if (!schoolId || !schedule.enabled) return undefined;
    if (scheduleSaving || scheduleRunning) return undefined;

    const dueKey = [
      schoolId,
      schedule.delivery_day,
      schedule.delivery_time,
      schedule.last_period_key || '',
      'due',
    ].join('|');

    if (schedule.is_due_now) {
      if (scheduleAutoRunKeyRef.current === dueKey) return undefined;
      scheduleAutoTimerRef.current = setTimeout(() => {
        if (canManageReports) {
          runDueSchedule();
        } else {
          loadSchedule();
        }
      }, 500);
      return () => {
        if (scheduleAutoTimerRef.current) {
          clearTimeout(scheduleAutoTimerRef.current);
          scheduleAutoTimerRef.current = null;
        }
      };
    }

    if (schedule.skip_reason !== 'time not reached yet') return undefined;
    if (!schedule.delivery_time || !schedule.delivery_day) return undefined;

    const nowApp = nowInAppTz();
    const todayDay = Number(nowApp.getDate());
    if (todayDay !== Number(schedule.delivery_day)) return undefined;

    const [hh, mm] = String(schedule.delivery_time).split(':').map((v) => Number(v));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return undefined;

    const target = new Date(nowApp);
    target.setHours(hh, mm, 2, 0);
    const ms = target.getTime() - nowApp.getTime();
    if (ms <= 0) return undefined;

    scheduleAutoTimerRef.current = setTimeout(() => {
      loadSchedule();
    }, Math.min(ms, 6 * 60 * 60 * 1000));

    return () => {
      if (scheduleAutoTimerRef.current) {
        clearTimeout(scheduleAutoTimerRef.current);
        scheduleAutoTimerRef.current = null;
      }
    };
  }, [
    schoolId,
    canManageReports,
    schedule.enabled,
    schedule.delivery_day,
    schedule.delivery_time,
    schedule.last_period_key,
    schedule.skip_reason,
    schedule.is_due_now,
    scheduleSaving,
    scheduleRunning,
    loadSchedule,
    runDueSchedule,
  ]);

  useEffect(() => {
    if (!schoolId || tab !== 'monthly' || !schedule.enabled) return undefined;
    const interval = setInterval(() => {
      loadSchedule();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [schoolId, tab, schedule.enabled, loadSchedule]);

  useEffect(() => {
    if (!schoolId) return;
    api.get('/classes', { params: { school_id: schoolId } })
      .then((res) => setClasses(res.data?.data || []))
      .catch(() => setClasses([]));
    api.get('/settings/academic-years', { params: { school_id: schoolId } })
      .then((res) => {
        const list = res.data?.data || [];
        setSessions(list);
        if (!filters.academicYearId && list.length) {
          const current = list.find((y) => y.is_current) || list[0];
          if (current) setFilters((prev) => ({ ...prev, academicYearId: String(current.id) }));
        }
      })
      .catch(() => setSessions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  useEffect(() => {
    if (!filters.classId) {
      setSections([]);
      setFilters((prev) => ({ ...prev, sectionId: '' }));
      return;
    }
    api.get(`/classes/${filters.classId}`)
      .then((res) => setSections(res.data?.data?.sections || []))
      .catch(() => setSections([]));
  }, [filters.classId]);

  const canLoad = useMemo(() => {
    if (!schoolId) return false;
    if (!filters.academicYearId || !filters.classId) return false;
    if (tab === 'monthly' && !filters.month) return false;
    return true;
  }, [schoolId, filters.academicYearId, filters.classId, filters.month, tab]);

  const canPublishAll = useMemo(() => {
    if (!schoolId || !canManageReports || tab !== 'monthly') return false;
    return !!filters.academicYearId;
  }, [schoolId, filters.academicYearId, tab, canManageReports]);

  const paramsBase = useMemo(() => {
    const p = {
      school_id: schoolId,
      academic_year_id: filters.academicYearId ? Number(filters.academicYearId) : undefined,
      class_id: filters.classId ? Number(filters.classId) : undefined,
      section_id: filters.sectionId ? Number(filters.sectionId) : undefined,
      q: query.trim() || undefined,
    };
    Object.keys(p).forEach((k) => (p[k] === undefined ? delete p[k] : null));
    return p;
  }, [schoolId, filters.academicYearId, filters.classId, filters.sectionId, query]);

  const load = async () => {
    if (!canLoad) return;
    setLoading(true);
    setError('');
    try {
      const url = tab === 'annual' ? '/reports/attendance/annual' : '/reports/attendance/monthly';
      const p = { ...paramsBase };
      if (tab === 'monthly') p.month = Number(filters.month);
      const res = await api.get(url, { params: p });
      const d = res.data?.data || {};
      setData({
        working_days: d.working_days || 0,
        rows: d.rows || [],
        meta: d.meta || {},
      });
    } catch (e) {
      setData({ working_days: 0, rows: [], meta: {} });
      setError(e?.response?.data?.message || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters.academicYearId, filters.classId, filters.sectionId, filters.month]);

  const scheduleStatusMessage = useMemo(() => {
    if (!schedule.enabled) {
      return 'Automatic delivery is off.';
    }
    if (schedule.is_due_now) {
      return scheduleRunning
        ? 'Due now — publishing automatically…'
        : 'Due now — publishing on next check (within a few minutes).';
    }
    if (schedule.skip_reason === 'time not reached yet') {
      return `Waiting — delivery time not reached yet (${formatTime12h(schedule.delivery_time || '06:00')}).`;
    }
    if (schedule.skip_reason === 'wrong day') {
      return `Waiting — today is not day ${schedule.delivery_day} of the month.`;
    }
    if (schedule.skip_reason === 'already run for period') {
      return `Already delivered for period ${schedule.last_period_key || 'previous month'}. Use Run now to publish the next period.`;
    }
    if (schedule.skip_reason) {
      return `Status: ${schedule.skip_reason}`;
    }
    return '';
  }, [schedule, scheduleRunning]);

  const exportFile = async (kind) => {
    if (!canLoad) return;
    const isPdf = kind === 'pdf';
    const url = tab === 'annual'
      ? `/reports/attendance/annual/${kind}`
      : `/reports/attendance/monthly/${kind}`;
    const p = { ...paramsBase };
    if (tab === 'monthly') p.month = Number(filters.month);
    try {
      const res = await api.get(url, { params: p, responseType: 'blob' });
      await handleBlobDownloadResponse(
        res,
        isPdf ? 'pdf' : 'zip', // zip check is lenient enough for xlsx? no → handle manually below
        `${tab}-attendance.${kind}`,
      );
    } catch (e) {
      // XLSX is not zip-kind check; reuse message parse + direct download fallback
      try {
        if (!isPdf && e?.response?.data instanceof Blob) {
          // ignore
        }
      } catch { /* ignore */ }
      toast({
        title: 'Export failed',
        description: e?.message || e?.response?.data?.message || 'Try again',
        variant: 'destructive',
      });
    }
  };

  const exportXlsx = async () => {
    if (!canLoad) return;
    const url = tab === 'annual'
      ? '/reports/attendance/annual/xlsx'
      : '/reports/attendance/monthly/xlsx';
    const p = { ...paramsBase };
    if (tab === 'monthly') p.month = Number(filters.month);
    try {
      const res = await api.get(url, { params: p, responseType: 'blob' });
      // XLSX starts with PK, so zip validator OK
      await handleBlobDownloadResponse(res, 'zip', `${tab}-attendance.xlsx`);
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e?.message || e?.response?.data?.message || 'Try again',
        variant: 'destructive',
      });
    }
  };

  const previewParams = useMemo(() => {
    if (!previewStudent?.id) return null;
    const p = { ...paramsBase, student_id: previewStudent.id };
    delete p.q;
    if (tab === 'monthly') p.month = Number(filters.month);
    return p;
  }, [previewStudent, paramsBase, tab, filters.month]);

  const openPreview = (row) => {
    const sid = row.student_id;
    if (!sid || !canLoad) return;
    setPreviewStudent({
      id: sid,
      name: row.student_name || row.admission_no || 'Student',
    });
    setPreviewOpen(true);
  };

  const publishToAllClasses = async () => {
    if (!canPublishAll) return;
    setPublishingAll(true);
    try {
      const body = {
        school_id: schoolId,
        academic_year_id: Number(filters.academicYearId),
      };
      if (filters.month) body.month = Number(filters.month);
      const res = await api.post('/reports/attendance/monthly/publish-all', body);
      const r = res.data?.data || {};
      const classes = r.classes_processed ?? 0;
      const classesFailed = r.classes_failed ?? 0;
      const emailFailed = r.emails_failed ?? 0;
      const notifs = r.notifications_created ?? 0;
      const errList = Array.isArray(r.errors) ? r.errors : [];
      const errPreview = errList.slice(0, 3).join(' ');
      toast({
        title: classesFailed > 0 || emailFailed > 0 ? 'Published with issues' : 'Published to all classes',
        description: `${classes} class(es), ${formatEmailSummary(r)}, ${notifs} notification(s) for period ${r.period_key || 'previous month'}.${errPreview ? ` ${errPreview}${errList.length > 3 ? '…' : ''}` : ''}`,
        variant: classesFailed > 0 || emailFailed > 0 ? 'destructive' : 'default',
      });
      setPublishAllOpen(false);
    } catch (e) {
      toast({
        title: 'Publish failed',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setPublishingAll(false);
    }
  };

  const publishToClass = async () => {
    if (!canLoad || data.rows.length === 0) return;
    setPublishing(true);
    try {
      const url = tab === 'annual'
        ? '/reports/attendance/annual/publish'
        : '/reports/attendance/monthly/publish';
      const body = { ...paramsBase };
      if (tab === 'monthly') body.month = Number(filters.month);
      const res = await api.post(url, body);
      const r = res.data?.data || {};
      const processed = r.students_processed ?? 0;
      const saved = r.publications_saved ?? 0;
      const emailFailed = r.emails_failed ?? 0;
      const notifs = r.notifications_created ?? 0;
      const failed = r.students_failed ?? 0;
      const errList = Array.isArray(r.errors) ? r.errors : [];
      const errPreview = errList.slice(0, 3).join(' ');
      const parts = [
        `${processed} student(s)`,
        `${saved} saved to portal`,
        formatEmailSummary(r),
        `${notifs} notification(s)`,
        failed ? `${failed} student(s) failed` : null,
      ].filter(Boolean);
      toast({
        title: failed > 0 || emailFailed > 0 ? 'Published with issues' : 'Published to class',
        description: errPreview
          ? `${parts.join(', ')}. ${errPreview}${errList.length > 3 ? '…' : ''}`
          : parts.join(', '),
        variant: failed > 0 || emailFailed > 0 ? 'destructive' : 'default',
      });
      setPublishOpen(false);
    } catch (e) {
      toast({
        title: 'Publish failed',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  const publishLabel = tab === 'annual' ? 'Publish annual reports' : 'Publish monthly reports';
  const classLabel = [
    data.meta?.class_name,
    data.meta?.section_name,
  ].filter(Boolean).join(' — ') || 'selected class';
  const periodLabel = tab === 'annual'
    ? (data.meta?.academic_year_name || 'session')
    : (data.meta?.month_label || 'month');

  const printTable = () => {
    if (isNativePlatform()) {
      toast({
        title: 'Print not available',
        description: 'Use Export to download attendance reports on mobile.',
        variant: 'destructive',
      });
      return;
    }
    const w = window.open('', '_blank');
    if (!w) return;
    const title = tab === 'annual' ? 'Student Annual Attendance Report' : 'Student Monthly Attendance Report';
    const subtitle = [
      data.meta?.academic_year_name ? `Session: ${data.meta.academic_year_name}` : '',
      data.meta?.class_name ? `Class: ${data.meta.class_name}` : '',
      data.meta?.section_name ? `Section: ${data.meta.section_name}` : '',
      data.meta?.month_label ? `Month: ${data.meta.month_label}` : '',
    ].filter(Boolean).join(' | ');
    const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body{font-family:Arial, sans-serif; color:#111827; margin:16px;}
    h1{font-size:18px; margin:0 0 4px;}
    .sub{color:#6b7280; font-size:12px; margin:0 0 12px;}
    table{width:100%; border-collapse:collapse; font-size:12px;}
    th,td{border:1px solid #d1d5db; padding:6px 8px; text-align:left;}
    th{background:#f3f4f6;}
    .r{text-align:right;}
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="sub">${subtitle}</div>
  <div class="sub">Total Working Days: ${data.working_days || 0}</div>
  ${document.getElementById('attendance-report-table')?.outerHTML || ''}
</body>
</html>`;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Attendance Report</h1>
        <p className="text-muted-foreground mt-1">
          Monthly and annual student attendance statistics with export and printing.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
            <TabsTrigger value="annual">Annual</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={load} disabled={!canLoad || loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
            <Button variant="outline" onClick={printTable} disabled={data.rows.length === 0}>
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
            <Button variant="outline" onClick={() => exportFile('pdf')} disabled={!canLoad || loading || data.rows.length === 0}>
              <FileDown className="h-4 w-4 mr-2" /> PDF
            </Button>
            <Button variant="outline" onClick={exportXlsx} disabled={!canLoad || loading || data.rows.length === 0}>
              <FileDown className="h-4 w-4 mr-2" /> Excel
            </Button>
            {tab === 'monthly' && canManageReports && (
            <Button
              disabled={!canPublishAll || publishingAll || publishing}
              onClick={() => setPublishAllOpen(true)}
            >
              {publishingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Publish to All Classes
            </Button>
            )}
            <Button
              disabled={!canLoad || loading || publishing || publishingAll || data.rows.length === 0}
              onClick={() => setPublishOpen(true)}
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Publish to Class
            </Button>
          </div>
        </div>

        <AlertDialog open={publishAllOpen} onOpenChange={setPublishAllOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Publish monthly reports to all classes</AlertDialogTitle>
              <AlertDialogDescription>
                Send attendance report PDFs by email and notify students and parents in{' '}
                <strong>all active classes</strong> for{' '}
                <strong>{filters.month ? MONTHS.find((m) => String(m.value) === String(filters.month))?.label || 'selected month' : 'the previous calendar month'}</strong>.
                {' '}No class filter is required. This may take several minutes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={publishingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={publishingAll} onClick={(e) => { e.preventDefault(); publishToAllClasses(); }}>
                {publishingAll ? 'Publishing…' : 'Publish all'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{publishLabel}</AlertDialogTitle>
              <AlertDialogDescription>
                Send attendance report PDFs by email and notify students and parents in{' '}
                <strong>{classLabel}</strong> for <strong>{periodLabel}</strong>.
                {' '}This runs for <strong>{data.rows.length}</strong> student(s) and may take a minute.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={publishing} onClick={(e) => { e.preventDefault(); publishToClass(); }}>
                {publishing ? 'Publishing…' : 'Publish'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {tab === 'monthly' && (
          <Card className="rounded-2xl border-border shadow-sm mt-4">
            <CardContent className="p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Automatic monthly delivery</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Scheduled delivery publishes to <strong>all active classes</strong> automatically (email + parent/student portal).
                  On the selected day and time each month (India time), reports are delivered for the appropriate period.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Delivery runs automatically at the scheduled time (server cron every 15 minutes, or when this page is open).
                  Use Run now to test immediately.
                </p>
              </div>
              {scheduleLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading schedule…
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-lg border border-border p-4">
                    <div>
                      <p className="font-medium">Enable automatic delivery</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {schedule.enabled ? 'Scheduled reports will run on the delivery day and time.' : 'Only manual publish is used.'}
                      </p>
                    </div>
                    <Switch
                      checked={!!schedule.enabled}
                      disabled={!canManageReports}
                      onCheckedChange={(v) => setSchedule((prev) => ({ ...prev, enabled: v }))}
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-2xl">
                    <div className="space-y-2">
                      <Label>Delivery day of month</Label>
                      <Select
                        value={String(schedule.delivery_day || 1)}
                        disabled={!canManageReports}
                        onValueChange={(v) => setSchedule((prev) => ({ ...prev, delivery_day: Number(v) }))}
                      >
                        <SelectTrigger><SelectValue placeholder="Day" /></SelectTrigger>
                        <SelectContent>
                          {DELIVERY_DAYS.map((d) => (
                            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Delivery time</Label>
                      <TimeInput12h
                        value={schedule.delivery_time || '06:00'}
                        disabled={!canManageReports}
                        onChange={(v) => setSchedule((prev) => ({ ...prev, delivery_time: v }))}
                        triggerClassName="bg-muted border-border"
                      />
                    </div>
                    {schedule.next_delivery_hint && (
                      <div className="space-y-2">
                        <Label>Next scheduled run</Label>
                        <p className="text-sm pt-2">{schedule.next_delivery_hint}</p>
                      </div>
                    )}
                  </div>
                  {scheduleStatusMessage && (
                    <div className={`text-sm rounded-lg border p-3 ${
                      schedule.is_due_now
                        ? 'border-green-500/40 bg-green-500/10 text-green-800 dark:text-green-200'
                        : 'border-border bg-muted/20 text-muted-foreground'
                    }`}>
                      {scheduleStatusMessage}
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground rounded-lg bg-muted/30 p-3">
                    <p>
                      <span className="font-medium text-foreground">Last run:</span>{' '}
                      {schedule.last_run_at ? (
                        <>
                          {schedule.last_run_at}
                          {schedule.last_period_key ? ` (period ${schedule.last_period_key})` : ''}
                        </>
                      ) : (
                        'Never'
                      )}
                    </p>
                    {schedule.last_run_summary && (
                      <p className="mt-1">
                        {schedule.last_run_summary.classes_processed ?? 0} class(es),{' '}
                        {formatEmailSummary(schedule.last_run_summary)}
                        , {schedule.last_run_summary.notifications_created ?? 0} notification(s)
                      </p>
                    )}
                    {(schedule.last_run_summary?.emails_failed ?? 0) > 0 && (
                      <p className="mt-2 text-amber-700 dark:text-amber-300 text-xs">
                        Some emails failed. Check Settings → Email (SMTP) for this school.
                      </p>
                    )}
                  </div>
                  {canManageReports && (
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={saveSchedule} disabled={scheduleSaving || scheduleRunning}>
                        {scheduleSaving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Save schedule
                      </Button>
                      <Button
                        variant="outline"
                        onClick={runScheduleNow}
                        disabled={scheduleRunning || scheduleSaving}
                      >
                        {scheduleRunning ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4 mr-2" />
                        )}
                        Run now
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="rounded-2xl border-border shadow-sm mt-4">
          <CardContent className="p-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 p-4 rounded-xl border border-border bg-muted/10">
              <div className="space-y-2">
                <Label>Session</Label>
                <Select
                  value={filters.academicYearId || ''}
                  onValueChange={(v) => setFilters((prev) => ({ ...prev, academicYearId: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Academic year" /></SelectTrigger>
                  <SelectContent>
                    {sessions.map((y) => (
                      <SelectItem key={y.id} value={String(y.id)}>{y.name}{y.is_current ? ' (Current)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Class</Label>
                <Select
                  value={filters.classId || ''}
                  onValueChange={(v) => setFilters((prev) => ({ ...prev, classId: v, sectionId: '' }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Section</Label>
                <Select
                  value={filters.sectionId || 'all'}
                  onValueChange={(v) => setFilters((prev) => ({ ...prev, sectionId: v === 'all' ? '' : v }))}
                  disabled={!filters.classId}
                >
                  <SelectTrigger><SelectValue placeholder="All sections" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sections</SelectItem>
                    {sections.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <TabsContent value="monthly" className="m-0">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select
                    value={filters.month || ''}
                    onValueChange={(v) => setFilters((prev) => ({ ...prev, month: v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="annual" className="m-0" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Input
                type="search"
                className="max-w-sm"
                placeholder="Search admission no or student name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="text-sm text-muted-foreground">
                Working days: <span className="font-medium text-foreground">{formatNumber(data.working_days)}</span>
                {' '}| Students: <span className="font-medium text-foreground">{data.rows.length}</span>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-500">{error}</div>
            )}
            {!error && !canLoad && (
              <div className="text-sm text-muted-foreground">
                Select session, class{tab === 'monthly' ? ', and month' : ''} to load report.
              </div>
            )}

            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table id="attendance-report-table" className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="p-3 text-left">Admission No</th>
                      <th className="p-3 text-left">Student Name</th>
                      <th className="p-3 text-right">Working Days</th>
                      <th className="p-3 text-right">Present</th>
                      <th className="p-3 text-right">Absent</th>
                      <th className="p-3 text-right">%</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Loading report…</td></tr>
                    ) : data.rows.length === 0 ? (
                      <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No data</td></tr>
                    ) : (
                      data.rows.map((r) => (
                        <tr key={r.student_id || `${r.admission_no}-${r.student_name}`} className="border-t border-border hover:bg-muted/20">
                          <td className="p-3">{r.admission_no || '—'}</td>
                          <td className="p-3 font-medium">{r.student_name || '—'}</td>
                          <td className="p-3 text-right">{formatNumber(r.working_days)}</td>
                          <td className="p-3 text-right">{formatNumber(r.present_days)}</td>
                          <td className="p-3 text-right">{formatNumber(r.absent_days)}</td>
                          <td className="p-3 text-right">{formatNumber(r.attendance_percentage)}</td>
                          <td className="p-3">
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                title="Preview"
                                disabled={loading || !r.student_id || !canLoad}
                                onClick={() => openPreview(r)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </Tabs>

      <AttendanceReportPreviewModal
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) setPreviewStudent(null);
        }}
        mode={tab}
        params={previewParams}
        studentLabel={previewStudent?.name || 'Student'}
      />
    </div>
  );
}

